import { useEffect, useState } from "react";
import { api, formatErr, validityStatus, toneClass, openDataUrlInTab } from "@/lib/api";
import { PageHeader, ExportBar, VerifiedTag, ValidityBadge } from "@/components/EntityPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, UserCog, Upload, X, FileText } from "lucide-react";

function readFile(file) { return new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(file); }); }

function DocUpload({ label, value, onChange, testId }) {
  async function pick(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3_000_000) { toast.error("File too large (max 3MB)"); return; }
    onChange(await readFile(file));
  }
  const isPdf = value && value.startsWith("data:application/pdf");
  return (
    <div>
      <Label className="text-[10px] uppercase text-muted-foreground">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-[hsl(224_40%_12%)] hover:border-amber-500/40 text-xs">
          <Upload className="w-3.5 h-3.5"/>{value ? "Replace" : "Browse"}
          <input type="file" accept="image/*,application/pdf" onChange={pick} className="hidden" data-testid={testId}/>
        </label>
        {value && (
          isPdf ? (
            <button type="button" onClick={() => openDataUrlInTab(value)} className="inline-flex items-center gap-1 text-xs text-sky-300 border border-sky-500/40 rounded-md px-2 py-1.5 hover:bg-sky-500/10">
              <FileText className="w-3.5 h-3.5"/> View PDF
            </button>
          ) : (
            <button type="button" onClick={() => openDataUrlInTab(value)} title="Click to open"><img src={value} alt="" className="w-14 h-14 object-cover rounded-md border border-border hover:border-amber-500/40"/></button>
          )
        )}
        {value && <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange("")}><X className="w-3.5 h-3.5"/></Button>}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">Max 3 MB · JPG/PNG/PDF</div>
    </div>
  );
}

function DriverForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || {});
  const [users, setUsers] = useState([]);
  const [copyWA, setCopyWA] = useState(!!initial && initial.mobile && initial.mobile === initial.whatsapp);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role === "driver"))).catch(() => {});
  }, []);
  function onMobile(e) {
    const v = e.target.value;
    setF((s) => ({ ...s, mobile: v, whatsapp: copyWA ? v : s.whatsapp }));
  }
  function toggleCopy(v) {
    setCopyWA(!!v);
    if (v) setF((s) => ({ ...s, whatsapp: s.mobile || s.whatsapp }));
  }
  async function pick(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 800_000) return toast.error("Image too large (max ~800KB)");
    setF({ ...f, photo_url: await readFile(file) });
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-slate-700 overflow-hidden grid place-items-center">
          {f.photo_url ? <img src={f.photo_url} alt="" className="w-full h-full object-cover"/> : <UserCog className="w-6 h-6 text-slate-400"/>}
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Passport photo</Label>
          <Input type="file" accept="image/*" onChange={pick} data-testid="driver-photo" className="mt-1 bg-[hsl(224_40%_12%)]"/>
          <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase text-muted-foreground">Name</Label><Input required value={f.name || ""} onChange={set("name")} data-testid="driver-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Mobile</Label><Input value={f.mobile || ""} onChange={onMobile} data-testid="driver-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">WhatsApp</Label>
          <Input value={f.whatsapp || ""} onChange={set("whatsapp")} disabled={copyWA} data-testid="driver-whatsapp" className={`bg-[hsl(224_40%_12%)] mt-1 ${copyWA ? "opacity-70" : ""}`}/>
          <label className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
            <input type="checkbox" checked={copyWA} onChange={(e) => toggleCopy(e.target.checked)} data-testid="driver-wa-same" className="accent-amber-500 h-3.5 w-3.5"/>
            Same as mobile — auto-copy
          </label>
        </div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Family mobile</Label><Input value={f.family_mobile || ""} onChange={set("family_mobile")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">DL number</Label><Input value={f.dl_number || ""} onChange={set("dl_number")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">DL valid till</Label><Input type="date" value={f.dl_validity || ""} onChange={set("dl_validity")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Aadhaar</Label><Input value={f.aadhaar || ""} onChange={set("aadhaar")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="flex items-end gap-4">
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!f.dl_verified} onChange={(e) => setF({ ...f, dl_verified: e.target.checked })} className="accent-amber-500"/> DL verified</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!f.aadhaar_verified} onChange={(e) => setF({ ...f, aadhaar_verified: e.target.checked })} className="accent-amber-500"/> Aadhaar verified</label>
        </div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Address</Label><Input value={f.address || ""} onChange={set("address")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="col-span-2 pt-3 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mb-2">Driver documents (visible on driver portal)</div>
          <div className="grid grid-cols-3 gap-3">
            <DocUpload label="Driving Licence copy" value={f.dl_image || ""} onChange={(v) => setF({ ...f, dl_image: v })} testId="driver-dl-doc"/>
            <DocUpload label="Aadhaar card copy" value={f.aadhaar_image || ""} onChange={(v) => setF({ ...f, aadhaar_image: v })} testId="driver-aadhaar-doc"/>
            <DocUpload label="Authority letter" value={f.authority_letter_image || ""} onChange={(v) => setF({ ...f, authority_letter_image: v })} testId="driver-authority-doc"/>
          </div>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-amber-300/80">Link to login account (role: driver)</Label>
          <select value={f.linked_user_id || ""} onChange={(e) => setF({ ...f, linked_user_id: e.target.value || null })}
            data-testid="driver-linked-user"
            className="w-full mt-1 h-9 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
            <option value="">— not linked —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Pick the login account created in Users &amp; Roles. Once linked, that user sees this driver's trips, KPIs, and can request withdrawals.</div>
        </div>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="driver-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update" : "Create"}</Button></div>
    </form>
  );
}

export default function Drivers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() { const { data } = await api.get("/drivers"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function save(f) {
    try {
      if (editing) await api.put(`/drivers/${editing.id}`, f); else await api.post("/drivers", f);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/drivers/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Drivers" subtitle="Driver roster with DL & Aadhaar verification and passport photo."
        right={<>
          <ExportBar rows={filtered.map((d) => ({ name: d.name, dl_number: d.dl_number, dl_valid: d.dl_validity, mobile: d.mobile, whatsapp: d.whatsapp }))} filename="drivers" whatsappText={`Drivers: ${filtered.length}`}/>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-drivers-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New driver</Button></DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "New"} driver</DialogTitle></DialogHeader>
              <DriverForm initial={editing} onSubmit={save}/>
            </DialogContent>
          </Dialog>
        </>}
      />
      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter drivers…" data-testid="filter-input" className="bg-transparent border-0 focus-visible:ring-0 h-8"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
              {["Photo","Driver","DL","DL Valid","Aadhaar","Mobile","WhatsApp","Actions"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No records</td></tr> :
                filtered.map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="p-3">{d.photo_url ? <img src={d.photo_url} alt="" className="w-10 h-10 rounded-full object-cover"/> : <div className="w-10 h-10 rounded-full bg-slate-700 grid place-items-center text-xs">{d.name?.[0]}</div>}</td>
                    <td className="p-3">{d.name}</td>
                    <td className="p-3"><div className="flex flex-col gap-1"><span className="font-mono text-xs text-amber-300">{d.dl_number || "—"}</span><VerifiedTag ok={d.dl_verified}/></div></td>
                    <td className="p-3"><ValidityBadge date={d.dl_validity}/></td>
                    <td className="p-3"><div className="flex flex-col gap-1"><span className="font-mono text-xs">{d.aadhaar || "—"}</span><VerifiedTag ok={d.aadhaar_verified}/></div></td>
                    <td className="p-3 font-mono text-xs">{d.mobile || "—"}</td>
                    <td className="p-3 font-mono text-xs">{d.whatsapp || "—"}</td>
                    <td className="p-3"><div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" data-testid={`edit-driver-${d.id}`} className="h-8 w-8" onClick={() => { setEditing(d); setOpen(true); }}><Pencil className="w-3.5 h-3.5"/></Button>
                      <Button variant="ghost" size="icon" data-testid={`del-driver-${d.id}`} className="h-8 w-8 hover:text-red-400" onClick={() => del(d.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                    </div></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
